
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Form,
} from '@/components/ui/form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useDashboard } from '@/hooks/useDashboard';
import { profileSchema, ProfileFormValues } from '@/lib/schemas/profileSchema';
import { updateUserProfile } from '@/lib/utils/profileUtils';
import ProfileFormFields from './ProfileFormFields';
import PasswordChangeSection from './PasswordChangeSection';

const AccountSettingsDialog: React.FC = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [passwordChangeMode, setPasswordChangeMode] = useState(false);
  const { isProfileOpen, setIsProfileOpen, userData, refreshUserStatus } = useDashboard();

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      fullName: userData?.full_name || '',
      phoneNumber: userData?.phone || '',  // Changed from phone_number to phone
      companyName: userData?.company_name || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      email: userData?.email || '',
    },
  });

  // Reset form when dialog opens with latest user data
  useEffect(() => {
    if (isProfileOpen && userData) {
      form.reset({
        fullName: userData.full_name || '',
        phoneNumber: userData.phone || '',  // Changed from phone_number to phone
        companyName: userData.company_name || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        email: userData.email || '',
      });
      setPasswordChangeMode(false);
    }
  }, [isProfileOpen, userData, form]);

  const handleSubmit = async (values: ProfileFormValues) => {
    setIsLoading(true);
    
    try {
      await updateUserProfile(values, userData);
      
      toast({
        title: 'Profile Updated',
        description: 'Your profile information has been updated successfully',
      });
      
      setIsProfileOpen(false);
      refreshUserStatus();
    } catch (error: any) {
      console.error('Profile update error:', error);
      toast({
        title: 'Update Failed',
        description: error.message || 'There was a problem updating your profile',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsProfileOpen(false);
    form.reset();
  };

  return (
    <Dialog open={isProfileOpen} onOpenChange={setIsProfileOpen}>
      <DialogContent className="sm:max-w-md" aria-labelledby="account-settings-title">
        <DialogHeader>
          <DialogTitle id="account-settings-title">Account Settings</DialogTitle>
          <DialogDescription>
            Update your profile information and account settings
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <ProfileFormFields form={form} />
            
            <PasswordChangeSection 
              form={form}
              passwordChangeMode={passwordChangeMode}
              setPasswordChangeMode={setPasswordChangeMode}
            />
            
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AccountSettingsDialog;
